import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import AdminLayout from '../../components/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Percent, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function AdminVATSettings() {
  const { language, BACKEND_URL, apiHeaders } = useApp();
  const [vatRate, setVatRate] = useState(10);
  const [accRate, setAccRate] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = language === 'tr' ? 'Vergi Ayarları | Amorium Admin' : 'Tax Settings | Amorium Admin';
    fetchTaxes();
  }, [language]);

  const fetchTaxes = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/settings/taxes`);
      setVatRate(res.data.vat_rate);
      setAccRate(res.data.accommodation_tax_rate);
    } catch (err) {
      console.error('Error fetching taxes:', err);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    const v = parseFloat(vatRate);
    const a = parseFloat(accRate);
    if (isNaN(v) || v < 0 || v > 100) {
      toast.error(language === 'tr' ? 'KDV oranı 0-100 arasında olmalıdır' : 'VAT rate must be 0-100');
      return;
    }
    if (isNaN(a) || a < 0 || a > 100) {
      toast.error(language === 'tr' ? 'Konaklama vergisi 0-100 arasında olmalıdır' : 'Accommodation tax must be 0-100');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/taxes`,
        { vat_rate: v, accommodation_tax_rate: a },
        apiHeaders(),
      );
      toast.success(language === 'tr' ? 'Vergi oranları güncellendi' : 'Tax rates updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'tr' ? 'Güncelleme hatası' : 'Update error'));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-[#6E7F5B]">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
        </div>
      </AdminLayout>
    );
  }

  const sample = 1000;
  const vatAmt = sample * (parseFloat(vatRate) / 100);
  const accAmt = sample * (parseFloat(accRate) / 100);
  const total = sample + vatAmt + accAmt;

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto p-6" data-testid="admin-vat-settings-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#1F3A2E] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {language === 'tr' ? 'Vergi Ayarları' : 'Tax Settings'}
          </h1>
          <p className="text-[#6E7F5B]">
            {language === 'tr'
              ? 'Geceleme hizmetleri için KDV ve Konaklama Vergisi oranlarını yönetin'
              : 'Manage VAT and Accommodation Tax rates applied to accommodation services'}
          </p>
        </div>

        <Card className="p-6 border-[#EFE7DA]">
          <div className="space-y-6">
            {/* Info Alert */}
            <div className="bg-[#fef3c7] border border-[#fde68a] rounded-lg p-4 flex gap-3">
              <AlertCircle size={20} className="text-[#92400e] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#92400e]">
                <p className="font-semibold mb-1">
                  {language === 'tr' ? '📌 Türk Vergi Mevzuatı' : '📌 Turkish Tax Regulations'}
                </p>
                <p>
                  {language === 'tr'
                    ? 'Türkiye\'de otel geceleme hizmetlerinde KDV %10 ve Konaklama Vergisi %2 uygulanır (2024 itibarıyla). Bu iki vergi, oda ücreti (KDV hariç subtotal) üzerinden AYRI AYRI hesaplanır ve toplama eklenir.'
                    : 'In Turkey, hotel accommodation services are subject to 10% VAT and 2% Accommodation Tax (as of 2024). These two taxes are calculated SEPARATELY on the room rate (subtotal, excluding VAT) and added to the total.'}
                </p>
              </div>
            </div>

            {/* VAT Rate Input */}
            <div className="space-y-2">
              <Label htmlFor="vat-rate" className="text-sm font-semibold text-[#1F3A2E]">
                {language === 'tr' ? 'KDV Oranı (%)' : 'VAT Rate (%)'}
              </Label>
              <div className="relative max-w-xs">
                <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6E7F5B]" size={18} />
                <Input
                  id="vat-rate"
                  data-testid="vat-rate-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  className="pl-10 border-[#D8D1C6] focus:border-[#1F3A2E]"
                  placeholder="10"
                />
              </div>
            </div>

            {/* Accommodation Tax Rate Input */}
            <div className="space-y-2">
              <Label htmlFor="acc-rate" className="text-sm font-semibold text-[#1F3A2E]">
                {language === 'tr' ? 'Konaklama Vergisi (%)' : 'Accommodation Tax (%)'}
              </Label>
              <div className="relative max-w-xs">
                <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6E7F5B]" size={18} />
                <Input
                  id="acc-rate"
                  data-testid="accommodation-tax-rate-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={accRate}
                  onChange={(e) => setAccRate(e.target.value)}
                  className="pl-10 border-[#D8D1C6] focus:border-[#1F3A2E]"
                  placeholder="1"
                />
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="tax-save-btn"
              className="bg-[#1F3A2E] hover:bg-[#3E5A3F] text-white flex items-center gap-2"
            >
              <Save size={16} />
              {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? 'Kaydet' : 'Save')}
            </Button>

            {/* Current Application */}
            <div className="bg-[#F6F1E7] rounded-lg p-4 space-y-2">
              <h3 className="text-sm font-semibold text-[#1F3A2E]">
                {language === 'tr' ? '📊 Örnek Hesaplama' : '📊 Sample Calculation'}
              </h3>
              <div className="space-y-1 text-sm text-[#6E7F5B]">
                <div className="flex justify-between">
                  <span>{language === 'tr' ? 'Oda Ücreti:' : 'Room Rate:'}</span>
                  <span className="font-semibold text-[#1F3A2E]">₺{sample.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>KDV ({vatRate}%):</span>
                  <span className="font-semibold text-[#1F3A2E]">₺{vatAmt.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{language === 'tr' ? 'Konaklama Vergisi' : 'Accommodation Tax'} ({accRate}%):</span>
                  <span className="font-semibold text-[#1F3A2E]">₺{accAmt.toFixed(2)}</span>
                </div>
                <div className="border-t border-[#D8D1C6] pt-1 mt-2 flex justify-between">
                  <span className="font-bold text-[#1F3A2E]">
                    {language === 'tr' ? 'Toplam (Vergiler Dahil):' : 'Total (Incl. Taxes):'}
                  </span>
                  <span className="font-bold text-[#1F3A2E]">₺{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="text-xs text-[#6E7F5B] space-y-1">
              <p>
                {language === 'tr'
                  ? '• Bu ayarlar tüm oda fiyatlandırmalarında otomatik olarak uygulanır'
                  : '• These settings are automatically applied to all room pricing'}
              </p>
              <p>
                {language === 'tr'
                  ? '• Mevcut rezervasyonlar etkilenmez, sadece yeni hesaplamalar güncellenir'
                  : '• Existing reservations are not affected, only new calculations are updated'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
